/**
 * ─────────────────────────────────────────────
 * src/hooks/useAuth.js
 * ─────────────────────────────────────────────
 *
 * WHY THIS FILE EXISTS:
 * Without this hook, every component that needs auth state would write:
 *
 *   import { useSelector, useDispatch } from 'react-redux'
 *   import { loginUser, logout, clearAuthErrors } from '../store/slices/authSlice'
 *
 *   const dispatch = useDispatch()
 *   const { user, token, isLoginLoading, loginError } = useSelector(state => state.auth)
 *
 * That's 4 lines of boilerplate EVERY time. In 10 components = 40 lines of boilerplate.
 *
 * WITH this hook, any component just writes:
 *   const { user, token, isLoginLoading, loginError, login, logoutUser } = useAuth()
 *
 * ONE import, everything available. Clean, reusable, testable.
 *
 * WHAT IS useSelector?
 * It reads from the Redux store. When the store updates, the component re-renders.
 * It's like useState but the state is GLOBAL (shared across all components).
 *
 * WHAT IS useDispatch?
 * It gives you the dispatch function.
 * dispatch(action) is how you trigger state changes in Redux.
 * Think of it as "send a message to the Redux store".
 */

import { useDispatch, useSelector } from "react-redux";
import {
  loginUser,
  forgotPassword,
  verifyOtp,
  resetPassword,
  setPassword,
  logout,
  clearAuthErrors,
  clearAuthSuccess,
  clearResetSession,
} from "../store/slices/authSlice";

const useAuth = () => {
  // dispatch: the function to trigger Redux actions
  const dispatch = useDispatch();

  // useSelector reads from store.auth (the key we defined in store/index.js)
  // Destructure exactly what we need
  const {
    user,
    token,
    resetEmail,
    resetOtpToken,
    resetOtpVerified,
    isLoginLoading,
    isForgotLoading,
    isOtpLoading,
    isResetLoading,
    isSetPasswordLoading,
    loginError,
    forgotError,
    otpError,
    resetError,
    setPasswordError,
    forgotPasswordSuccess,
    otpVerified,
    resetPasswordSuccess,
    setPasswordSuccess,
  } = useSelector((state) => state.auth);

  // isAuthenticated: computed from whether token and user both exist
  const isAuthenticated = !!(token && user);

  // ─────────────────────────────────────────
  // ACTION WRAPPERS
  // These are functions components call.
  // They dispatch the async thunks.
  //
  // WHY wrap dispatch in a function?
  // So components don't need to import dispatch or the thunks themselves.
  // The hook hides Redux details. Components just call: login(credentials)
  // ─────────────────────────────────────────

  const login = (credentials) => dispatch(loginUser(credentials));

  const sendOtp = (email) => dispatch(forgotPassword({ email }));

  const confirmOtp = (email, otp) => dispatch(verifyOtp({ email, otp }));

  const newPassword = (email, password) =>
    dispatch(resetPassword({ email, password }));

  const activateAccount = (token, password) =>
    dispatch(setPassword({ token, password }));

  const logoutUser = () => dispatch(logout());

  const clearErrors = () => dispatch(clearAuthErrors());

  const clearSuccess = () => dispatch(clearAuthSuccess());

  const clearReset = () => dispatch(clearResetSession());

  // Return everything the component might need
  return {
    // ── State ─────────────────────────────
    user,
    token,
    isAuthenticated,

    // ── Reset flow state ──────────────────
    resetEmail,
    resetOtpToken,
    resetOtpVerified,

    // ── Loading flags ──────────────────────
    isLoginLoading,
    isForgotLoading,
    isOtpLoading,
    isResetLoading,
    isSetPasswordLoading,

    // ── Errors ────────────────────────────
    loginError,
    forgotError,
    otpError,
    resetError,
    setPasswordError,

    // ── Success flags ─────────────────────
    forgotPasswordSuccess,
    otpVerified,
    resetPasswordSuccess,
    setPasswordSuccess,

    // ── Actions ───────────────────────────
    login,
    sendOtp,
    confirmOtp,
    newPassword,
    activateAccount,
    logoutUser,
    clearErrors,
    clearSuccess,
    clearReset,
  };
};

export default useAuth;
